/* ═══════════════════════════════════════════════════════════════════════════
   BRØNNØYSUNDREGISTRENE

   Until now `orgOk` in domain/signup.js could say only that nine digits
   satisfy the mod-11 arithmetic. Its own comment set out the limit:

     "it does not say the number exists, it does not say it belongs to this
      business … that needs Brønnøysund, and nothing here talks to them."

   Now something does. Enhetsregisteret's open API needs no key, no
   registration and no agreement, and it answers with the business's real
   name, its registered address, whether it is in the VAT register, and — from
   a second endpoint — who its daglig leder is.

   ── Why this matters more than saving the tenant some typing ─────────────

   Three things on this platform were resting on the tenant's own word, and
   one of them was resting on nothing at all:

     the ORGANISATION NUMBER is a required field on every sales document, and
       a typo'd one that happens to pass mod-11 is a valid-looking number
       belonging to a different company

     "MVA" after that number is a CLAIM. domain/salesdoc.js already refuses to
       infer it and takes the business's own answer instead — and the business
       is guessing as often as not. `registrertIMvaregisteret` is the actual
       answer, from the actual register

     the demonstration catalogue carries thirty-four organisation numbers and
       thirty-three of them were invented by the prototype. A lookup is how
       they stop being invented

   ── What this module does NOT do ─────────────────────────────────────────

   Decide. Everything here returns a PROPOSAL, and the screen puts it in front
   of the tenant to accept or reject. Autofill that writes straight into the
   record is autofill that silently replaces a carefully typed trading address
   with a registered one the post never reaches — and the two are different
   often enough to matter, because plenty of businesses are registered at
   their accountant's address.

   ── And it says when the answer is bad news ──────────────────────────────

   A company can be registered, findable, and bankrupt. `warnings()` reads
   `konkurs`, `underAvvikling`, `underTvangsavviklingEllerTvangsopplosning`
   and `slettedato`, because a platform that autofilled a dissolved company's
   details and said nothing would be helping somebody trade under a company
   that no longer exists.
   ═══════════════════════════════════════════════════════════════════════ */
import { orgOk, orgWords } from './signup.js';

export const BASE = 'https://data.brreg.no/enhetsregisteret/api';

/** Nine digits, however the tenant typed them. */
export const digits = v => String(v ?? '').replace(/\D/g, '');

export const looksLikeOrgNo = v => /^\d{9}$/.test(digits(v));

/**
 * The query to send, given whatever is in the box.
 *
 * Nine digits is a number and goes to the single-entity route; anything else
 * is a name and goes to the search. Asking the tenant which they typed would
 * be asking them to do the one piece of work a computer is better at.
 */
export function queryFor(input) {
  const raw = String(input ?? '').trim();
  if (!raw) return null;
  if (looksLikeOrgNo(raw)) return { kind: 'number', path: `/enheter/${digits(raw)}` };
  return {
    kind: 'name',
    path: `/enheter?navn=${encodeURIComponent(raw)}&size=10`,
  };
}

/** The entities out of either answer, so the caller never unwraps twice. */
export const entitiesIn = body => {
  if (!body) return [];
  if (Array.isArray(body?._embedded?.enheter)) return body._embedded.enheter;
  if (body.organisasjonsnummer) return [body];
  return [];
};

/* ── One entity, as this platform sees it ─────────────────────────────── */

/**
 * The fields we care about, named the way our own records name them.
 *
 * `forretningsadresse` is the business address and `postadresse` is where the
 * post goes; they are often the same and the first is the one a customer
 * would walk to, so it wins. A business that only has a postal address gets
 * that rather than nothing, and the screen says which it used.
 */
export function readEntity(e = {}) {
  const biz = e.forretningsadresse || null;
  const post = e.postadresse || null;
  const addr = biz || post;

  return {
    org_no: digits(e.organisasjonsnummer) || null,
    name: e.navn || null,
    form: e.organisasjonsform?.kode || null,
    formWords: e.organisasjonsform?.beskrivelse || null,
    street: streetOf(addr),
    postcode: addr?.postnummer || '',
    city: titleCase(addr?.poststed || ''),
    municipality: titleCase(addr?.kommune || ''),
    country: addr?.landkode || 'NO',
    /* Which of the two addresses this came from, said on the screen — a
       tenant registered at their accountant's office needs to know that is
       what they are being offered. */
    addressFrom: biz ? 'business' : post ? 'postal' : null,
    /* The actual answer to the question domain/salesdoc.js refuses to guess
       at. Note the distinction Brønnøysund makes and we keep: this is the VAT
       register, not the Foretaksregister. */
    vat_registered: e.registrertIMvaregisteret === true,
    /* Brønnøysund's own words for the trade. Deliberately NOT mapped onto our
       own industry list: a mapping table invented here would put a business
       in the wrong trade with an authoritative-looking source behind it. The
       tenant reads the description and picks. */
    trade: e.naeringskode1?.beskrivelse || null,
    tradeCode: e.naeringskode1?.kode || null,
    staffCount: Number.isFinite(e.antallAnsatte) ? e.antallAnsatte : null,
    site: e.hjemmeside || null,
    since: e.registreringsdatoEnhetsregisteret || null,
    /* Kept whole so `warnings` reads the register rather than a summary of
       it, and so a state nobody thought about is still in the object. */
    raw: {
      konkurs: e.konkurs === true,
      underAvvikling: e.underAvvikling === true,
      tvangsavvikling: e.underTvangsavviklingEllerTvangsopplosning === true,
      slettedato: e.slettedato || null,
    },
  };
}

const streetOf = addr => {
  const lines = Array.isArray(addr?.adresse) ? addr.adresse.filter(Boolean) : [];
  return lines.join(', ');
};

/* Brønnøysund shouts: "OSLO", "LYKKE SALONG AS". A shouted city on a receipt
   reads as a mistake, so it is cased back — but only where the whole string
   is upper case, because an acronym the register spells correctly must not be
   quietly lower-cased. */
export function titleCase(s) {
  const t = String(s ?? '').trim();
  if (!t || t !== t.toUpperCase()) return t;
  return t.toLowerCase().replace(/(^|[\s\-/'])([a-zà-ÿ])/g, (_, a, b) => a + b.toUpperCase());
}

/**
 * What is wrong with this company, if anything.
 *
 * A company can be registered, findable, and bankrupt. Saying nothing would
 * be helping somebody trade under a company that no longer exists.
 */
export function warnings(hit) {
  if (!hit) return [];
  const r = hit.raw || {};
  const out = [];
  if (r.slettedato) {
    out.push(`This company was removed from the register on ${r.slettedato}. `
      + 'It no longer exists.');
  }
  if (r.konkurs) out.push('This company is registered as bankrupt.');
  if (r.tvangsavvikling) out.push('This company is under compulsory dissolution.');
  else if (r.underAvvikling) out.push('This company is being wound up.');
  /* A sole trader is a person, and the platform's documents say "the
     business". Worth flagging once rather than being a surprise on a
     receipt. */
  if (hit.form === 'ENK') {
    out.push('This is an enkeltpersonforetak — the proprietor is personally '
      + 'the business, and the sales document will name it exactly as it is '
      + 'registered.');
  }
  return out;
}

/** Whether anything found is bad enough that it should not simply be used. */
export const blocking = hit => {
  const r = hit?.raw || {};
  return !!(r.slettedato || r.konkurs || r.tvangsavvikling);
};

/* ── Who runs it ──────────────────────────────────────────────────────── */

/* The role codes worth reading. Daglig leder is the one Stein asked for;
   the chair is the answer for a company that has no daglig leder, which is
   most small AS companies where the owner sits on the board instead. */
export const ROLES = [
  ['DAGL', 'Managing director'],
  ['LEDE', 'Chair of the board'],
  ['INNH', 'Proprietor'],
];

/**
 * The people out of `/enheter/{orgnr}/roller`.
 *
 * Only the roles above, only people who have not resigned, and only their
 * names — the register publishes more and none of the rest is any of this
 * platform's business.
 */
export function readRoles(body = {}) {
  const out = [];
  (body.rollegrupper || []).forEach(group => {
    const code = group?.type?.kode;
    const known = ROLES.find(([k]) => k === code);
    if (!known) return;
    (group.roller || []).forEach(role => {
      if (role?.fratraadt) return;
      const p = role.person?.navn;
      /* A role can be held by another COMPANY rather than by a person — an
         accountancy firm as daglig leder, for instance. Both are real
         answers and the screen shows whichever it is. */
      const name = p
        ? [p.fornavn, p.mellomnavn, p.etternavn].filter(Boolean).join(' ')
        : role.enhet?.navn || null;
      if (!name) return;
      out.push({ role: code, word: known[1], name: titleCase(name), person: !!p });
    });
  });
  /* In the order ROLES lists them, so the managing director is first. */
  return out.sort((a, b) =>
    ROLES.findIndex(([k]) => k === a.role) - ROLES.findIndex(([k]) => k === b.role));
}

export const leaderOf = people => people.find(p => p.role === 'DAGL') || people[0] || null;

/* ── The proposal ─────────────────────────────────────────────────────── */

/**
 * What would change if the tenant accepted this, field by field.
 *
 * A list rather than a merged object, because the screen offers them one at a
 * time. A tenant who typed their trading address and is being shown the one
 * they are registered at — their accountant's, often — must be able to take
 * the organisation number and leave the address, and a merged object cannot
 * express that.
 */
export function proposal(hit, business = {}) {
  if (!hit) return [];
  const rows = [
    ['org_no', 'Organisation number', hit.org_no, business.org_no],
    ['name', 'Registered name', hit.name, business.name],
    ['street', 'Street address', hit.street, business.street],
    ['postcode', 'Postcode', hit.postcode, business.postcode],
    ['city', 'Town', hit.city, business.city],
    ['vat_registered', 'In the VAT register', hit.vat_registered, business.vat_registered],
  ];
  return rows
    .filter(([, , next]) => next !== null && next !== undefined && next !== '')
    .map(([id, word, next, now]) => ({
      id,
      word,
      next,
      now: now === undefined ? '' : now,
      /* Whether accepting this would overwrite something the tenant wrote
         themselves, which is the one case worth a second look. */
      replaces: !!(now !== '' && now !== null && now !== undefined
        && String(now) !== String(next)),
      /* Shown as words rather than as `true`. */
      shows: typeof next === 'boolean' ? (next ? 'Yes' : 'No') : String(next),
      was: typeof now === 'boolean' ? (now ? 'Yes' : 'No') : String(now ?? ''),
    }));
}

/**
 * A line saying what a looked-up number now means, and what it still does
 * not.
 *
 * `orgOk` says the arithmetic works. This says Brønnøysund has it. Neither
 * says the person at the keyboard is entitled to act for the company, and the
 * screen must not let anybody read it that way — the register is public, so
 * anyone can look up anyone.
 */
export const checkedWords = hit => (hit?.org_no
  ? `${orgWords(hit.org_no)} is registered to ${hit.name} in `
    + 'Enhetsregisteret. That the number exists and belongs to that company is '
    + 'all this confirms — the register is public, so a look-up is not proof '
    + 'that you act for it.'
  : null);

/** The arithmetic check, kept together with the register one. */
export { orgOk, orgWords };
